import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  ReceivingAttempt,
  ReceivingCharge,
  ReceivingChargeInput,
  ReceivingChargeKind,
  ReceivingConnection,
  ReceivingConnectionInput,
  ReceivingIssueInput,
  ReceivingOverview,
  ReceivingPage,
  ReceivingPayer,
  ReceivingPayerInput,
  ReceivingReport,
  ReceivingReportFilters,
} from "~/types/financeReceiving";

export default class FinanceReceivingModule extends HttpFactory {
  getOverview(signal?: AbortSignal): Promise<ReceivingOverview> {
    return this.get(`${ENDPOINTS.FINANCE.RECEIVING}overview/`, {}, { signal });
  }

  getCharges(
    filters: { student_id?: number; page?: number; competence?: string },
    signal?: AbortSignal,
  ): Promise<ReceivingPage<ReceivingCharge>> {
    return this.get(
      `${ENDPOINTS.FINANCE.RECEIVING}charges/`,
      { ...filters },
      { signal },
    );
  }

  createCharge(input: ReceivingChargeInput): Promise<ReceivingCharge> {
    return this.post(`${ENDPOINTS.FINANCE.RECEIVING}charges/`, input);
  }

  mutateCharge(
    kind: ReceivingChargeKind,
    id: number,
    action: "settle" | "reopen" | "cancel",
    input: {
      expected_version: number;
      client_request_id: string;
      confirm_active_attempt?: boolean;
    },
  ): Promise<ReceivingCharge> {
    return this.post(
      `${ENDPOINTS.FINANCE.RECEIVING}charges/${kind}/${id}/${action}/`,
      input,
    );
  }

  createConnection(
    input: ReceivingConnectionInput,
  ): Promise<ReceivingConnection> {
    return this.post(`${ENDPOINTS.FINANCE.RECEIVING}connections/`, input);
  }

  deactivateConnection(
    id: string,
    input: { expected_version: number; client_request_id: string },
  ): Promise<ReceivingConnection> {
    return this.post(
      `${ENDPOINTS.FINANCE.RECEIVING}connections/${encodeURIComponent(id)}/deactivate/`,
      input,
    );
  }

  getPayers(
    studentId: number,
    signal?: AbortSignal,
  ): Promise<ReceivingPage<ReceivingPayer>> {
    return this.get(
      `${ENDPOINTS.FINANCE.RECEIVING}payers/`,
      { student_id: studentId },
      { signal },
    );
  }

  createPayer(input: ReceivingPayerInput): Promise<ReceivingPayer> {
    return this.post(`${ENDPOINTS.FINANCE.RECEIVING}payers/`, input);
  }

  issue(
    kind: ReceivingChargeKind,
    id: number,
    input: ReceivingIssueInput,
  ): Promise<ReceivingAttempt> {
    return this.post(
      `${ENDPOINTS.FINANCE.RECEIVING}charges/${kind}/${id}/issue/`,
      input,
    );
  }

  refreshAttempt(id: string): Promise<ReceivingAttempt> {
    return this.post(
      `${ENDPOINTS.FINANCE.RECEIVING}attempts/${encodeURIComponent(id)}/refresh/`,
      {},
    );
  }

  cancelAttempt(
    id: string,
    input: { client_request_id: string },
  ): Promise<ReceivingAttempt> {
    return this.post(
      `${ENDPOINTS.FINANCE.RECEIVING}attempts/${encodeURIComponent(id)}/cancel/`,
      input,
    );
  }

  getReport(
    filters: ReceivingReportFilters,
    signal?: AbortSignal,
  ): Promise<ReceivingReport> {
    return this.get(
      `${ENDPOINTS.FINANCE.RECEIVING}reports/`,
      { ...filters },
      { signal },
    );
  }

  getReportCsv(filters: ReceivingReportFilters): Promise<Blob> {
    return this.get(
      `${ENDPOINTS.FINANCE.RECEIVING}reports/`,
      { ...filters, format: "csv" },
      { responseType: "blob" },
    );
  }

  getMyPayments(
    page = 1,
    signal?: AbortSignal,
  ): Promise<ReceivingPage<ReceivingAttempt>> {
    return this.get(ENDPOINTS.FINANCE.MY_PAYMENTS, { page }, { signal });
  }

  getMyPayment(id: string, signal?: AbortSignal): Promise<ReceivingAttempt> {
    return this.get(
      `${ENDPOINTS.FINANCE.MY_PAYMENTS}${encodeURIComponent(id)}/`,
      {},
      { signal },
    );
  }
}
